import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';

function TakeExam() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [showFlaggedWarning, setShowFlaggedWarning] = useState(false);
  const [showQuestionOverview, setShowQuestionOverview] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [examEndTime, setExamEndTime] = useState(null);
  const [timerExpired, setTimerExpired] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionTimings, setQuestionTimings] = useState({});
  const [timerHidden, setTimerHidden] = useState(false);

  // Inject CSS animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { opacity: 0; transform: translateY(-20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .exam-svg-container svg {
        width: 100% !important;
        height: auto !important;
        max-height: 400px;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    apiClient.get(`/exams/${id}`)
      .then(response => {
        setExam(response.data);
        const now = Date.now();
        const durationMs = response.data.duration * 60 * 1000;
        setExamEndTime(now + durationMs);
        setTimeRemaining(response.data.duration * 60);
        setStartTime(now);
        setQuestionStartTime(now);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading exam:', err);
        setLoading(false);
      });
  }, [id]);

  // Warn before leaving page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!submitting) {
        const message = 'Are you sure you want to leave? Your exam progress will be lost.';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [submitting]);

  // Block navigation attempts
  useEffect(() => {
    const handleClick = (e) => {
      if (submitting) return;
      let target = e.target;
      while (target && target !== document) {
        if (target.tagName === 'A' || target.tagName === 'BUTTON') {
          const isExamNavigation = target.closest('.exam-navigation') || 
                                   target.closest('.exam-top-bar') ||
                                   target.closest('.exam-bottom-bar') ||
                                   target.closest('.question-overview-modal');
          if (!isExamNavigation && target.tagName === 'A') {
            e.preventDefault();
            e.stopPropagation();
            const href = target.getAttribute('href');
            setPendingNavigation(href);
            setShowExitWarning(true);
            return false;
          }
        }
        target = target.parentElement;
      }
    };

    const handlePopState = (e) => {
      if (!submitting) {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
        setPendingNavigation('back');
        setShowExitWarning(true);
      }
    };

    window.history.pushState(null, '', window.location.href);
    document.addEventListener('click', handleClick, true);
    window.addEventListener('popstate', handlePopState);
    return () => {
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [submitting]);

  const handleStayOnExam = () => {
    setShowExitWarning(false);
    setPendingNavigation(null);
  };

  const handleLeaveExam = () => {
    setShowExitWarning(false);
    setSubmitting(true);
    const destination = pendingNavigation && pendingNavigation !== 'back' ? pendingNavigation : '/dashboard';
    navigate(destination);
  };

  // Timer countdown
  useEffect(() => {
    if (examEndTime === null || submitting) return;
    const timer = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((examEndTime - now) / 1000));
      setTimeRemaining(remaining);
      if (remaining <= 0) setTimerExpired(true);
    }, 100);
    return () => clearInterval(timer);
  }, [examEndTime, submitting]);

  useEffect(() => {
    if (timerExpired && !submitting) handleSubmit();
  }, [timerExpired]);

  const recordQuestionTime = (questionId) => {
    if (questionStartTime && questionId) {
      const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
      setQuestionTimings(prev => ({
        ...prev,
        [questionId]: (prev[questionId] || 0) + timeSpent
      }));
    }
  };

  const goToQuestion = (newIndex) => {
    if (exam && exam.questions[currentQ]) {
      recordQuestionTime(exam.questions[currentQ].id);
    }
    setCurrentQ(newIndex);
    setQuestionStartTime(Date.now());
  };

  const toggleFlag = (questionIndex) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionIndex)) newSet.delete(questionIndex);
      else newSet.add(questionIndex);
      return newSet;
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleSubmit = async () => {
    if (flaggedQuestions.size > 0) {
      setShowFlaggedWarning(true);
      return;
    }
    await submitExam();
  };

  const submitExam = async () => {
    if (exam && exam.questions[currentQ]) {
      recordQuestionTime(exam.questions[currentQ].id);
    }
    setSubmitting(true);
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      const response = await apiClient.post('/submit', { 
        examId: id, answers, timeSpent, questionTimings 
      });
      navigate(`/results/${response.data.resultId}`);
    } catch (err) {
      console.error('Error submitting exam:', err);
      alert('Failed to submit exam. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading exam...</p>
      </div>
    );
  }
  
  if (!exam) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
        <h2>Exam not found</h2>
      </div>
    );
  }

  const question = exam.questions[currentQ];
  const answeredCount = Object.keys(answers).filter(k => answers[k] !== '').length;
  const isLastQuestion = currentQ === exam.questions.length - 1;
  const isWritingTest = question.type === 'written';

  // Reading test passage extraction
  const isReadingTest = exam.topic === 'Reading' || exam.topic === 'OC_Reading';
  let passageText = null;
  let questionTextOnly = question.text;
  
  if (isReadingTest) {
    const referenceMatch = question.text.match(/\[Same (?:passage|poem|texts) as Question (\d+)\]/i);
    if (referenceMatch) {
      const refQuestionNum = parseInt(referenceMatch[1]) - 1;
      if (exam.questions[refQuestionNum]) {
        const refQuestion = exam.questions[refQuestionNum];
        const parts = refQuestion.text.split('\n\n');
        if (parts.length > 1) {
          passageText = parts.slice(0, -1).join('\n\n');
          questionTextOnly = parts[parts.length - 1];
        }
      }
      questionTextOnly = question.text.replace(/\[Same (?:passage|poem|texts) as Question \d+\]\s*\n*/i, '').trim();
    } else {
      const parts = question.text.split('\n\n');
      if (parts.length > 1) {
        passageText = parts.slice(0, -1).join('\n\n');
        questionTextOnly = parts[parts.length - 1];
      }
    }
  }

  // Shared styles
  const navyBg = '#1e3a5f';
  const lightBlueBg = '#dce6f0';
  const panelBg = '#f0f4f8';
  const dividerColor = '#b0c4d8';

  // Render answer options (shared between layouts)
  const renderOptions = () => {
    if (!question.options) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {question.options.map((option, idx) => {
          const isSelected = answers[question.id] === option.id;
          return (
            <label key={idx} style={{ 
              display: 'flex', alignItems: 'center', padding: '12px 16px',
              background: 'white', border: isSelected ? '2px solid #2563eb' : '1px solid #c5d3e0',
              borderRadius: '4px', cursor: 'pointer',
              transition: 'border-color 0.15s'
            }}>
              <input type="radio" name={`q${question.id}`} checked={isSelected}
                onChange={() => handleAnswer(question.id, option.id)}
                style={{ marginRight: '14px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2563eb' }}
              />
              <span style={{ fontSize: '15px', color: '#1f2937', lineHeight: '1.5' }}>{option.text}</span>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: lightBlueBg,
      width: '100%',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Arial, Helvetica, sans-serif'
    }}>
      {/* === TOP BAR (NSW Style - Dark Navy) === */}
      <div className="exam-top-bar" style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: navyBg,
        padding: '8px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: '52px'
      }}>
        {/* Left: Timer + Hide time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!timerHidden && (
            <>
              <span style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: timeRemaining < 300 ? '#fca5a5' : 'white',
                fontFamily: 'monospace',
                letterSpacing: '2px'
              }}>
                {Math.floor((timeRemaining || 0) / 3600).toString().padStart(2, '0')} : {Math.floor(((timeRemaining || 0) % 3600) / 60).toString().padStart(2, '0')}
              </span>
              <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>Hours &nbsp; Mins</span>
            </>
          )}
          <button
            onClick={() => setTimerHidden(!timerHidden)}
            className="exam-navigation"
            style={{
              padding: '4px 12px',
              fontSize: '12px',
              fontWeight: '600',
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {timerHidden ? 'Show time' : 'Hide time'}
          </button>
        </div>

        {/* Center: Question counter + grid icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>
            Question {currentQ + 1} of {exam.questions.length}
          </span>
          <button
            onClick={() => setShowQuestionOverview(true)}
            className="exam-navigation"
            style={{
              padding: '5px 8px',
              fontSize: '20px',
              background: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1
            }}
            title="Question Overview"
          >
            ⊞
          </button>
        </div>

        {/* Right: Leave Exam button */}
        <button
          onClick={() => { setPendingNavigation('/dashboard'); setShowExitWarning(true); }}
          className="exam-navigation"
          style={{
            padding: '6px 16px',
            fontSize: '13px',
            fontWeight: '600',
            background: 'rgba(37,99,235,0.8)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Leave Exam
        </button>
      </div>

      {/* === MAIN CONTENT AREA - Split Screen for ALL subjects === */}
      <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
        
        {/* === LEFT PANEL: Question / Passage / Prompt === */}
        <div style={{
          flex: '0 0 50%',
          padding: '28px 30px',
          background: panelBg,
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 116px)',
          borderRight: `2px solid ${dividerColor}`
        }}>
          {isReadingTest && passageText ? (
            /* Reading: Show passage text */
            <div style={{
              fontSize: '15px',
              lineHeight: '1.9',
              color: '#1f2937',
              whiteSpace: 'pre-wrap'
            }}>
              {passageText}
            </div>
          ) : (
            /* Math / Thinking / Writing: Show question text */
            <div>
              <p style={{ 
                fontSize: '15px', lineHeight: '1.8', color: '#1f2937', margin: 0,
                whiteSpace: 'pre-wrap'
              }}>
                {questionTextOnly}
              </p>

              {/* SVG Diagram (Math/Thinking questions) */}
              {question.imageSvg && (
                <div style={{
                  margin: '24px 0', padding: '24px', background: 'white',
                  borderRadius: '8px', border: `1px solid ${dividerColor}`,
                  display: 'flex', justifyContent: 'center', alignItems: 'center'
                }}>
                  <div dangerouslySetInnerHTML={{ __html: question.imageSvg }} style={{ 
                    width: '100%',
                    maxWidth: '550px',
                    minHeight: '200px'
                  }} className="exam-svg-container" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* === CENTER DIVIDER ARROW === */}
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          width: '24px',
          height: '24px',
          background: 'white',
          border: `1px solid ${dividerColor}`,
          borderRadius: '3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          color: navyBg,
          cursor: 'default'
        }}>
          ▶
        </div>

        {/* === RIGHT PANEL: Options / Written Answer === */}
        <div style={{
          flex: 1,
          padding: '28px 30px',
          background: lightBlueBg,
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 116px)'
        }}>
          {isReadingTest ? (
            /* Reading: question text + options on right */
            <div>
              <p style={{ 
                fontSize: '15px', lineHeight: '1.7', color: '#1f2937',
                margin: '0 0 20px 0'
              }}>
                {questionTextOnly}
              </p>
              {renderOptions()}
            </div>
          ) : isWritingTest ? (
            /* Writing: toolbar + rich text area on right (NSW style) */
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'white', border: `1px solid ${dividerColor}`, borderRadius: '4px', overflow: 'hidden' }}>
              {/* Toolbar matching NSW writing UI */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '6px 10px',
                background: '#f0f0f0',
                borderBottom: `1px solid ${dividerColor}`,
                flexWrap: 'wrap'
              }}>
                {/* Undo / Redo */}
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 8px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '14px', color: '#555' }} title="Undo">↩</button>
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 8px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '14px', color: '#555' }} title="Redo">↪</button>
                <div style={{ width: '1px', height: '20px', background: '#ccc', margin: '0 6px' }} />
                {/* Font Size dropdown */}
                <select className="exam-navigation" style={{ padding: '3px 6px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '13px', background: 'white', cursor: 'pointer' }} title="Font Size">
                  <option>Font Sizes</option>
                  <option>Small</option>
                  <option>Medium</option>
                  <option>Large</option>
                </select>
                <div style={{ width: '1px', height: '20px', background: '#ccc', margin: '0 6px' }} />
                {/* B I U */}
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 10px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', color: '#333' }} title="Bold">B</button>
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 10px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '14px', fontStyle: 'italic', color: '#333' }} title="Italic">I</button>
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 10px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline', color: '#333' }} title="Underline">U</button>
                <div style={{ width: '1px', height: '20px', background: '#ccc', margin: '0 6px' }} />
                {/* Alignment */}
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 8px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', color: '#555' }} title="Align Left">≡</button>
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 8px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', color: '#555' }} title="Align Center">≡</button>
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 8px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', color: '#555' }} title="Align Right">≡</button>
                <button type="button" onClick={() => {}} className="exam-navigation" style={{ padding: '4px 8px', background: 'white', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', fontSize: '13px', color: '#555' }} title="Justify">≡</button>
              </div>
              {/* Text area */}
              <textarea
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Type your answer here..."
                style={{
                  flex: 1,
                  width: '100%',
                  minHeight: '400px',
                  padding: '20px',
                  border: 'none',
                  fontSize: '15px',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  lineHeight: '1.8',
                  resize: 'none',
                  boxSizing: 'border-box',
                  background: 'white',
                  outline: 'none'
                }}
              />
            </div>
          ) : (
            /* Math / Thinking: "What is the answer?" + options on right */
            <div>
              {/* Answer recorded indicator */}
              {answers[question.id] !== undefined && answers[question.id] !== '' && (
                <div style={{
                  background: '#bfdbfe',
                  padding: '6px 14px',
                  fontSize: '13px',
                  color: '#1e40af',
                  fontWeight: '500',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }}>
                  ✓ Your answer was recorded.
                </div>
              )}
              {renderOptions()}
            </div>
          )}
        </div>
      </div>

      {/* === BOTTOM BAR (NSW Style - Dark Navy) === */}
      <div className="exam-bottom-bar" style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        background: navyBg,
        padding: '0',
        display: 'flex',
        alignItems: 'stretch',
        minHeight: '48px'
      }}>
        {/* Left: Back button */}
        <button
          onClick={() => goToQuestion(currentQ - 1)}
          disabled={currentQ === 0}
          className="exam-navigation"
          style={{
            padding: '12px 28px',
            fontSize: '16px',
            fontWeight: '700',
            background: currentQ === 0 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.15)',
            color: currentQ === 0 ? 'rgba(255,255,255,0.3)' : 'white',
            border: 'none',
            borderRight: '1px solid rgba(255,255,255,0.15)',
            cursor: currentQ === 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ◀ &nbsp;Back
        </button>

        {/* Center spacer */}
        <div style={{ flex: 1 }} />

        {/* Flag button */}
        <button
          onClick={() => toggleFlag(currentQ)}
          className="exam-navigation"
          style={{
            padding: '12px 28px',
            fontSize: '16px',
            fontWeight: '700',
            background: flaggedQuestions.has(currentQ) ? 'rgba(251,191,36,0.3)' : 'transparent',
            color: 'white',
            border: 'none',
            borderLeft: '1px solid rgba(255,255,255,0.15)',
            borderRight: '1px solid rgba(255,255,255,0.15)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          Flag &nbsp;▼
        </button>

        {/* Next or Finish (on last question) */}
        {isLastQuestion ? (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="exam-navigation"
            style={{
              padding: '12px 28px',
              fontSize: '16px',
              fontWeight: '700',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {submitting ? 'Submitting...' : 'Finish ▶'}
          </button>
        ) : (
          <button
            onClick={() => goToQuestion(currentQ + 1)}
            className="exam-navigation"
            style={{
              padding: '12px 28px',
              fontSize: '16px',
              fontWeight: '700',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Next &nbsp;▶
          </button>
        )}
      </div>

      {/* === QUESTION OVERVIEW MODAL === */}
      {showQuestionOverview && (
        <div className="question-overview-modal" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000
        }}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '30px',
            maxWidth: '600px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.2s ease-out'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                Question Overview
              </h3>
              <button onClick={() => setShowQuestionOverview(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280', padding: '4px' }}>
                ✕
              </button>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', fontSize: '13px', color: '#4b5563', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '20px', height: '20px', background: 'white', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                Unattempted
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '20px', height: '20px', background: '#93c5fd', borderRadius: '3px' }} />
                Attempted
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '20px', height: '20px', background: '#fbbf24', borderRadius: '3px' }} />
                Flagged
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '20px', height: '20px', background: '#2563eb', borderRadius: '3px' }} />
                Current
              </div>
            </div>

            {/* Question Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: '6px',
              marginBottom: '20px'
            }}>
              {exam.questions.map((q, idx) => {
                const isAnswered = answers[q.id] !== undefined && answers[q.id] !== '';
                const isFlagged = flaggedQuestions.has(idx);
                const isCurrent = idx === currentQ;
                
                let bgColor = 'white';
                let borderColor = '#d1d5db';
                let textColor = '#374151';
                if (isCurrent) { bgColor = '#2563eb'; borderColor = '#2563eb'; textColor = 'white'; }
                else if (isFlagged) { bgColor = '#fbbf24'; borderColor = '#fbbf24'; textColor = '#1f2937'; }
                else if (isAnswered) { bgColor = '#93c5fd'; borderColor = '#93c5fd'; textColor = '#1f2937'; }
                
                return (
                  <button key={idx} onClick={() => { goToQuestion(idx); setShowQuestionOverview(false); }}
                    style={{
                      padding: '8px 4px', background: bgColor, color: textColor,
                      border: `1px solid ${borderColor}`, borderRadius: '4px',
                      fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      transition: 'all 0.1s'
                    }}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>

            {/* Finish button inside overview */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {answeredCount} of {exam.questions.length} answered
              </span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setShowQuestionOverview(false)}
                  style={{
                    padding: '8px 24px', fontSize: '14px', fontWeight: '600',
                    background: '#f3f4f6', border: '1px solid #d1d5db',
                    borderRadius: '4px', cursor: 'pointer', color: '#374151'
                  }}>
                  Close
                </button>
                <button onClick={() => { setShowQuestionOverview(false); handleSubmit(); }}
                  style={{
                    padding: '8px 24px', fontSize: '14px', fontWeight: '600',
                    background: navyBg, color: 'white', border: 'none',
                    borderRadius: '4px', cursor: 'pointer'
                  }}>
                  Finish Exam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === FLAGGED QUESTIONS WARNING MODAL === */}
      {showFlaggedWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '35px',
            maxWidth: '500px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%', background: '#fef3c7',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px'
              }}>⚠️</div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                Flagged Questions
              </h2>
            </div>

            <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.6', marginBottom: '16px' }}>
              You have <strong>{flaggedQuestions.size} question(s)</strong> flagged for review
              {exam.questions.length - answeredCount > 0 && (
                <span> and <strong>{exam.questions.length - answeredCount} unanswered question(s)</strong></span>
              )}.
            </p>

            <div style={{
              padding: '12px 15px', background: '#fef3c7', borderRadius: '6px',
              border: '1px solid #fbbf24', marginBottom: '16px', fontSize: '14px', color: '#92400e', fontWeight: '600'
            }}>
              📌 Questions: {Array.from(flaggedQuestions).map(i => i + 1).join(', ')}
            </div>

            <p style={{
              fontSize: '13px', color: '#6b7280', marginBottom: '20px', padding: '10px',
              background: '#f9fafb', borderRadius: '6px', borderLeft: '3px solid #3b82f6'
            }}>
              💡 Click "Review" to go back and check them before submitting.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowFlaggedWarning(false); setShowQuestionOverview(true); }}
                style={{
                  padding: '10px 22px', background: '#10b981', color: 'white', border: 'none',
                  borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
                }}>
                Review
              </button>
              <button onClick={() => { setShowFlaggedWarning(false); submitExam(); }}
                style={{
                  padding: '10px 22px', background: '#ef4444', color: 'white', border: 'none',
                  borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
                }}>
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === EXIT WARNING MODAL === */}
      {showExitWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '35px',
            maxWidth: '500px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%', background: '#fee2e2',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px'
              }}>⚠️</div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                Leave Exam?
              </h2>
            </div>

            <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.6', marginBottom: '8px' }}>
              Are you sure you want to leave this exam?
            </p>
            <p style={{ fontSize: '14px', color: '#dc2626', fontWeight: '600', marginBottom: '24px' }}>
              ⚠️ Your progress will be lost and the exam will not be submitted.
            </p>

            {timeRemaining !== null && (
              <div style={{
                background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '6px',
                padding: '12px 15px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px'
              }}>
                <span style={{ fontSize: '20px' }}>⏱️</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                    Time Remaining: {formatTime(timeRemaining)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#78350f' }}>
                    Click "Stay" to continue your test
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={handleStayOnExam}
                style={{
                  padding: '10px 28px', background: '#10b981', color: 'white', border: 'none',
                  borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
                }}>
                Stay
              </button>
              <button onClick={handleLeaveExam}
                style={{
                  padding: '10px 28px', background: '#ef4444', color: 'white', border: 'none',
                  borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
                }}>
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TakeExam;
